import React, { JSX, useMemo } from 'react';
import { CheckIcon, CloseIcon, TimeIcon } from '@chakra-ui/icons';
import { Box, Text, Tooltip } from '@chakra-ui/react';

import useTranslations from '../../../../contexts/LocalizationContext/hooks';
import { PaymentHistoryStatus } from '../model/enums/payment-history-status.enum';
import { PaymentHistoryResponse } from '../model/payment-history.response';

type PaymentStatusProps = {
  payment: PaymentHistoryResponse;
};

export const PaymentStatus = ({ payment }: PaymentStatusProps) => {
  const t = useTranslations();

  const statusColorsMap: Record<PaymentHistoryStatus, { bg: string; border: string; color: string }> = useMemo(() => {
    return {
      [PaymentHistoryStatus.SUCCESSFUL]: { bg: 'green.50', border: 'green.500', color: 'green.900' },
      [PaymentHistoryStatus.PENDING]: { bg: 'orange.50', border: 'orange.800', color: 'orange.800' },
      [PaymentHistoryStatus.FAILED]: { bg: 'red.50', border: 'red.700', color: 'red.700' },
    };
  }, []);

  const statusIconMap: Record<PaymentHistoryStatus, JSX.Element> = useMemo(
    () => ({
      [PaymentHistoryStatus.PENDING]: <TimeIcon boxSize={3} />,
      [PaymentHistoryStatus.SUCCESSFUL]: <CheckIcon boxSize={3} />,
      [PaymentHistoryStatus.FAILED]: <CloseIcon boxSize={3} />,
    }),
    [],
  );

  const statusTranslationKeyMap: Record<PaymentHistoryStatus, string> = useMemo(
    () => ({
      [PaymentHistoryStatus.PENDING]: 'users_eco_fee.payments.status_pending',
      [PaymentHistoryStatus.SUCCESSFUL]: 'users_eco_fee.payments.status_successful',
      [PaymentHistoryStatus.FAILED]: 'users_eco_fee.payments.status_failed',
    }),
    [],
  );

  const content = (
    <Box
      border={'1px'}
      borderColor={statusColorsMap[payment.status].border}
      bg={statusColorsMap[payment.status].bg}
      color={statusColorsMap[payment.status].color}
      borderRadius={'12px'}
      px={2}
      display="inline-flex"
      alignItems="center"
      gap={2}
    >
      {statusIconMap[payment.status]}
      <Text as="span" textTransform="capitalize">
        {t(statusTranslationKeyMap[payment.status])}
      </Text>
    </Box>
  );

  if (payment.status === PaymentHistoryStatus.PENDING) {
    return (
      <Tooltip
        placement="top"
        bg={'blueGrey.800'}
        hasArrow
        textAlign={'center'}
        label={t('users_eco_fee.payments.status_pending_tooltip')}
      >
        {content}
      </Tooltip>
    );
  }

  return content;
};
